import { AmountSuperScript } from "components/design/AmountSuperScript";
import {
  Accordion,
  ItemGrid,
} from "components/DesignSystem/Accordion/Accordion";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import Slider from "components/DesignSystem/Slider/Slider";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { DownloadIcon } from "components/icons/Download";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useGetRelatedLedgerEntryQuery } from "store/apis/generalLedger";
import { useGetFolderOrFileQuery } from "store/apis/previewUrl";
import { Transactions } from "types/Models/books";

type JournalEntryLogSliderProps = {
  isOpen: boolean;
  onClose: () => void;
  ledgerEntryId: string;
  ledgerEntrySerialNumber: string;
  title: string;
};

const Info = ({ transaction }: { transaction: Transactions }) => {
  const { amount, description, merchant, category } =
    transaction.transaction || {};

  return (
    <div className="t-rounded-lg t-border-neutral-10 t-border t-border-solid">
      <div className="t-p-4 t-flex t-gap-6 t-flex-col">
        <InfoItem label="Description">
          <>{description}</>
        </InfoItem>
        <ItemGrid>
          <InfoItem label="Vendor">{merchant}</InfoItem>

          <InfoItem label="Category">{category?.name}</InfoItem>

          <InfoItem label="Debit">
            <AmountSuperScript amount={Number(amount || 0)} />
          </InfoItem>
        </ItemGrid>
      </div>
    </div>
  );
};

const InvoiceView = ({
  invoiceId,
  groupId,
}: {
  invoiceId: string;
  groupId: string;
}) => {
  const { downloadFile, ...downloadState } = useDownloadFile();
  const { data, isSuccess, isLoading } = useGetFolderOrFileQuery(
    { groupId: groupId, fileId: invoiceId },
    { skip: !Boolean(invoiceId) || !groupId }
  );

  if (isLoading) {
    return <Skeleton className="t-h-8 t-w-full" />;
  }

  return (
    <FilePreviewTag size="small" file={data!} block>
      <Button
        customType="ghost_icon"
        size="small"
        disabled={downloadState.result.isFetching}
        isLoading={downloadState.result.isFetching}
        onClick={(e) => {
          e.stopPropagation();
          downloadFile({
            file_id: invoiceId,
            group_id: groupId,
          });
        }}
      >
        <DownloadIcon color="currentColor" />
      </Button>
    </FilePreviewTag>
  );
};

export const JournalEntryLogSlider = ({
  isOpen,
  onClose,
  ledgerEntryId,
  ledgerEntrySerialNumber,
  title,
}: JournalEntryLogSliderProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const {
    data: relatedLedgerEntry,
    isLoading,
    isSuccess,
  } = useGetRelatedLedgerEntryQuery(
    { groupId, entityId, ledger_entry_id: ledgerEntryId },
    { skip: !groupId || !entityId || !ledgerEntryId }
  );

  const JournalEntryItem = relatedLedgerEntry?.transactions?.filter(
    (item) =>
      item.transaction.ledger_entry_serial_number === ledgerEntrySerialNumber
  )[0];

  const isInvoiceLinked =
    JournalEntryItem?.invoices &&
    JournalEntryItem.invoices.length > 0 &&
    JournalEntryItem.invoices.every((invoice) => invoice.file_data);

  return (
    <Slider.Root open={isOpen} onOpenChange={onClose}>
      <Slider.Content
        open={isOpen}
        useCustomOverlay
        className="t-h-full"
        width={600}
      >
        <Async.Root
          {...{ isEmpty: false, isLoading, isSuccess }}
          renderOnLoad={
            <div className="t-flex t-flex-col t-gap-3 t-px-4 t-py-2 t-h-full">
              <Skeleton className="t-h-14" />
              <Skeleton className="t-h-2/6" />
              <Skeleton className="t-h-2/6" />
            </div>
          }
        >
          <Async.Empty>
            <></>
          </Async.Empty>
          <Async.Success>
            <Slider.Header>
              <Slider.Title>{title}</Slider.Title>
              <Slider.Close />
            </Slider.Header>
            <Slider.Body>
              <Accordion.Root
                defaultValue={"INVOICE_FILE"}
                type="single"
                className="t-flex t-flex-col t-gap-4"
                collapsible
              >
                <Info transaction={JournalEntryItem!} />
                <Accordion.Item value="INVOICE_FILE">
                  <Accordion.Trigger>Invoice Added</Accordion.Trigger>
                  <Accordion.Content>
                    {isInvoiceLinked ? (
                      JournalEntryItem?.invoices.map((invoice) => (
                        <div key={invoice.uuid} className="t-mb-2">
                          <InvoiceView
                            invoiceId={invoice.file_data?.uuid!}
                            groupId={groupId}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="t-flex t-items-center t-justify-center t-text-body-sm t-text-neutral-30">
                        No invoices added
                      </div>
                    )}
                  </Accordion.Content>
                </Accordion.Item>
              </Accordion.Root>
            </Slider.Body>
          </Async.Success>
        </Async.Root>
      </Slider.Content>
    </Slider.Root>
  );
};
